import { useQueryClient } from "@tanstack/react-query";
import Button from "components/buttons/Button/Button";
import { t } from "i18next";
import { useEffect, useState } from "react";

import { getValidationMessage } from "~/api/axiosUtils.ts";
import Headline from "~/components/Headline/Headline";
import Hint from "~/components/Hint";
import Modal from "~/components/Modal";
import Paragraph from "~/components/Paragraph";
import SidebarBusyOverlay from "~/components/Sidebar/components/SidebarBusyOverlay";
import { useAuth } from "~/contexts/AuthContext";
import {
	reopenMonthlyClosingNonProject,
	reopenMonthlyClosingStaffing,
} from "~/modules/timeTracking/api/monthlyClosing/monthlyClosingApiDispatchers.ts";
import { monthlyClosingQueries } from "~/modules/timeTracking/api/monthlyClosing/monthlyClosingQueries.ts";
import type { MonthlyClosingReopenRequest } from "~/modules/timeTracking/api/monthlyClosing/monthlyClosingTypes.ts";
import { TIME_TRACKINGS_BY_USER_AND_MONTH_QUERY_KEY } from "~/modules/timeTracking/api/timeTracking/timeTrackingQueries.ts";
import { normalizeTKey } from "~/types/typeHelpers.ts";

type Props = {
	/** `YYYY-MM`. */
	month: string;
	onClose: () => void;
	request: MonthlyClosingReopenRequest | null;
	userId: string;
};

export function ReopenMonthlyClosingModal(props: Props) {
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState("");

	const queryClient = useQueryClient();
	const { user } = useAuth();
	const reopensOwnClosing = user?.id === props.userId;

	useEffect(() => {
		if (props.request) setError("");
	}, [props.request]);

	const handleConfirmationClick = async () => {
		if (!props.request) return;
		const { part } = props.request;
		const refetchClosing = () =>
			queryClient.invalidateQueries({ queryKey: monthlyClosingQueries.month(props.userId, props.month).queryKey });
		setBusy(true);
		try {
			await (part.part === "staffing"
				? reopenMonthlyClosingStaffing(props.userId, props.month, part.staffingId)
				: reopenMonthlyClosingNonProject(props.userId, props.month));
			// The reply is a 204 with no body: the panel shows the open part only after the refetch.
			await refetchClosing();
			queryClient.invalidateQueries({ queryKey: monthlyClosingQueries.openMonths(props.userId).queryKey });
			// The closing decides the `update` and `delete` blocks of the trackings in the month.
			queryClient.invalidateQueries({ queryKey: TIME_TRACKINGS_BY_USER_AND_MONTH_QUERY_KEY(props.userId, props.month) });
			props.onClose();
		} catch (error) {
			setError(getValidationMessage(error, ["month"]) ?? "Ein unbekannter Fehler ist aufgetreten.");
			// A refusal comes from a stale page: the fresh read disables the button.
			await refetchClosing();
		} finally {
			setBusy(false);
		}
	};

	return (
		<Modal open={!!props.request}
			   setOpen={props.onClose}>
			<div className="relative flex flex-col gap-3">
				{busy && <SidebarBusyOverlay/> }
				<Headline type="h4">Monatsabschluss wieder öffnen</Headline>
				<div>
					{!error ? (
						<Hint theme="info">
							<Paragraph>Du öffnest {props.request?.label} wieder. Der Monatsabschluss sollte nur in
								Ausnahmefällen (z.B. bei versehentlicher Schließung){reopensOwnClosing
									? " rückgängig gemacht werden."
									: " und in Absprache mit dem jeweiligen Mitarbeiter rückgängig gemacht werden."}</Paragraph>
						</Hint>
					) : (
						<Hint theme="error">{error}</Hint>
					)}
				</div>
				<div className="mt-4">
					<Button disabled={busy || !!error}
							onClick={handleConfirmationClick}>
						Wieder öffnen
					</Button>
					<Button theme="none"
							onClick={props.onClose}>
						{t(normalizeTKey("form:buttonTexts.cancel"))}
					</Button>
				</div>
			</div>
		</Modal>
	);
}
